import React from 'react';
import { TextField, Grid, FormControlLabel, Checkbox, Autocomplete, Typography, Box, InputAdornment } from "@mui/material";
import Select from "react-select";
import { DEFAULT_MIN_PAYOUT } from "../../utils/constants";

const MerchantForm = ({ merchantIndex, values, handleChange, handleBlur, errors, touched, setFieldValue, isSubmitting, staticList, activeBusinessEntity, userOxygen7Entity, readOnly }) => {
    //Prefix for fields (eg items[0].name, items[0].address_line1)
    const prefix = `items[${merchantIndex}]`;

    const addressSameAsBE = values?.items?.[merchantIndex]?.address_as_business_entity || false;

    return (
        <Grid container spacing={2} alignItems="center">
            <Grid item xs={12}>
                <Typography variant="h6" my={1}>
                    Merchant #{merchantIndex + 1}
                </Typography>
            </Grid>

            {/* Merchant Name */}
            <Grid item xs={12} sm={3}>
                <Box display="flex" alignItems="center" justifyContent="flex-end" height="100%">
                    <Typography align="right">Merchant Name*</Typography>
                </Box>
            </Grid>
            <Grid item xs={12} sm={9}>
                <TextField
                    label="Merchant Name"
                    name={`${prefix}.name`}
                    value={values.items?.[merchantIndex]?.name || ''}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.items?.[merchantIndex]?.name && Boolean(errors.items?.[merchantIndex]?.name)}
                    helperText={touched.items?.[merchantIndex]?.name && errors.items?.[merchantIndex]?.name}
                    fullWidth
                    disabled={isSubmitting || readOnly}
                    size="small"
                    color="secondary"
                />
            </Grid>
            {/* Oxygen7 Entity */}
            <Grid item xs={12} sm={3}>
                <Box display="flex" alignItems="center" justifyContent="flex-end" height="100%">
                    <Typography align="right">Oxygen7 Entity</Typography>
                </Box>
            </Grid>
            <Grid item xs={12} sm={9}>
                <TextField
                    label="Oxygen7 Entity"
                    value={values.items?.[merchantIndex]?.oxygen7_entity?.label || userOxygen7Entity?.label || ''}
                    fullWidth
                    disabled
                    size="small"
                    color="secondary"
                    margin="dense"
                />
            </Grid>


            {/* Minimum Payout Amount */}
            <Grid item xs={12} sm={3}>
                <Box display="flex" alignItems="center" justifyContent="flex-end" height="100%">
                    <Typography align="right">Minimum Payout Amount</Typography>
                </Box>
            </Grid>
            <Grid item xs={12} sm={9}>
                <Select
                    isMulti
                    isDisabled={isSubmitting || readOnly}
                    options={DEFAULT_MIN_PAYOUT.map(c => ({ id: c.currency, currency: c.currency }))}
                    getOptionLabel={o => o.currency}
                    getOptionValue={o => o.id}
                    closeMenuOnSelect={false}
                    value={(values.items?.[merchantIndex]?.min_payout_amount || []).map(c => ({ id: c.currency, currency: c.currency }))}
                    onChange={selected => {
                        const selectedArray = selected || [];
                        const current = values.items?.[merchantIndex]?.min_payout_amount || [];
                        const newConfig = selectedArray.map(opt => {
                            const existing = current.find(c => c.currency === opt.id);
                            if (existing) return existing;
                            const def = DEFAULT_MIN_PAYOUT.find(d => d.currency === opt.id);
                            return { currency: opt.id, min_amount: def?.min_amount ?? "" };
                        });
                        setFieldValue(`${prefix}.min_payout_amount`, newConfig);
                    }}
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                    placeholder="Select currencies..."
                />
                {(values.items?.[merchantIndex]?.min_payout_amount || []).map((item, idx) => (
                    <TextField
                        key={item.currency}
                        sx={{ mt: 1, mr: 1 }}
                        label="Min Amount"
                        type="number"
                        size="small"
                        color="secondary"
                        inputProps={{ min: 0, step: 0.01 }}
                        InputLabelProps={{ shrink: true }}
                        InputProps={{ startAdornment: <InputAdornment position="start">{item.currency}</InputAdornment> }}
                        value={item.min_amount}
                        onChange={e => {
                            const updated = [...(values.items?.[merchantIndex]?.min_payout_amount || [])];
                            updated[idx] = { ...updated[idx], min_amount: e.target.value === '' ? '' : parseFloat(e.target.value) };
                            setFieldValue(`${prefix}.min_payout_amount`, updated);
                        }}
                        disabled={isSubmitting || readOnly}
                        variant="standard"
                    />
                ))}
            </Grid>

            {/* Number of Terminals */}
            <Grid item xs={12} sm={3}>
                <Box display="flex" alignItems="center" justifyContent="flex-end" height="100%">
                    <Typography align="right">Number of Terminals*</Typography>
                </Box>
            </Grid>
            <Grid item xs={12} sm={9}>
                <TextField
                    label="Number of Terminals"
                    name={`${prefix}.number_of_terminals`}
                    type="number"
                    value={values.items?.[merchantIndex]?.number_of_terminals || ''}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    fullWidth
                    disabled={isSubmitting || readOnly}
                    margin="dense"
                    size="small"
                    color="secondary"
                    inputProps={{ min: "1" }}
                    error={Boolean(
                        touched.items?.[merchantIndex]?.number_of_terminals &&
                        errors.items?.[merchantIndex]?.number_of_terminals
                    )}
                    helperText={
                        touched.items?.[merchantIndex]?.number_of_terminals &&
                        errors.items?.[merchantIndex]?.number_of_terminals
                    }
                />
            </Grid>

            {/* Average Ticket Size */}
            <Grid item xs={12} sm={3}>
                <Box display="flex" alignItems="center" justifyContent="flex-end" height="100%">
                    <Typography align="right">Average Ticket Size</Typography>
                </Box>
            </Grid>
            <Grid item xs={12} sm={9}>
                <Box display="flex" gap={1} alignItems="flex-start">
                    <TextField
                        select
                        label="Currency"
                        name={`${prefix}.average_ticket_size_currency`}
                        value={values.items?.[merchantIndex]?.average_ticket_size_currency || 'SGD'}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        sx={{ width: 110 }}
                        disabled={isSubmitting || readOnly}
                        margin="dense"
                        size="small"
                        color="secondary"
                        SelectProps={{ native: true }}
                    >
                        {DEFAULT_MIN_PAYOUT.map(c => (
                            <option key={c.currency} value={c.currency}>{c.currency}</option>
                        ))}
                    </TextField>
                    <TextField
                        label="Average Ticket Size"
                        name={`${prefix}.average_ticket_size`}
                        type="number"
                        value={values.items?.[merchantIndex]?.average_ticket_size || ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        fullWidth
                        disabled={isSubmitting || readOnly}
                        margin="dense"
                        size="small"
                        color="secondary"
                        inputProps={{ min: "0", step: "any" }}
                    />
                </Box>
            </Grid>

            {/* Can Void and Can Refund Checkboxes */}
            <Grid item xs={12} sm={3}>
                <Box display="flex" alignItems="center" justifyContent="flex-end" height="100%">
                    <Typography align="right">Permissions</Typography>
                </Box>
            </Grid>
            <Grid item xs={12} sm={9}>
                <Box display="flex" flexWrap="wrap" gap={2}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                name={`${prefix}.can_void`}
                                checked={Boolean(values.items?.[merchantIndex]?.can_void)}
                                onChange={handleChange}
                                disabled={isSubmitting || readOnly}
                            />
                        }
                        label="Can Void"
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                name={`${prefix}.can_refund`}
                                checked={Boolean(values.items?.[merchantIndex]?.can_refund)}
                                onChange={handleChange}
                                disabled={isSubmitting || readOnly}
                            />
                        }
                        label="Can Refund"
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                name={`${prefix}.can_void_via_merchant_portal`}
                                checked={Boolean(values.items?.[merchantIndex]?.can_void_via_merchant_portal)}
                                onChange={handleChange}
                                disabled={isSubmitting || readOnly}
                            />
                        }
                        label="Can Void via Merchant Portal"
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                name={`${prefix}.can_refund_via_merchant_portal`}
                                checked={Boolean(values.items?.[merchantIndex]?.can_refund_via_merchant_portal)}
                                onChange={handleChange}
                                disabled={isSubmitting || readOnly}
                            />
                        }
                        label="Can Refund via Merchant Portal"
                    />
                </Box>
            </Grid>

            {/* Checkbox: Address same as Business Entity */}
            <Grid item xs={12} sm={3}>
                <Box display="flex" alignItems="center" justifyContent="flex-end" height="100%">
                    <Typography align="right"> </Typography>
                </Box>
            </Grid>
            <Grid item xs={12} sm={9}>
                <FormControlLabel
                    control={
                        <Checkbox
                            name={`${prefix}.address_as_business_entity`}
                            checked={addressSameAsBE || false}
                            onChange={(e) => {
                                //Handle it manually because Formik is managing the state
                                setFieldValue(`${prefix}.address_as_business_entity`, e.target.checked);
                            }}
                            disabled={isSubmitting || readOnly}
                        />
                    }
                    label="The address is the same as the Business Entity"
                />
            </Grid>

            {/* Address Section Header */}
            {/* {!addressSameAsBE && (
                <Grid item xs={12}>
                    <Typography variant="h6" my={1}>
                        Merchant Address
                    </Typography>
                </Grid>
            )} */}

            {/*Address Fields (Hidden if Checkbox is selected) */}
            {!addressSameAsBE && (
                <>
                    <Grid item xs={12} sm={3}>
                        <Box display="flex" alignItems="center" justifyContent="flex-end" height="100%">
                            <Typography align="right">Address Line 1</Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={9}>
                        <TextField
                            label="Address Line 1"
                            name={`${prefix}.address_line1`}
                            value={values.items?.[merchantIndex]?.address_line1 || ''}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            fullWidth
                            disabled={isSubmitting || readOnly}
                            margin="dense"
                            size="small"
                            color="secondary"
                        />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <Box display="flex" alignItems="center" justifyContent="flex-end" height="100%">
                            <Typography align="right">Address Line 2</Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={9}>
                        <TextField
                            label="Address Line 2"
                            name={`${prefix}.address_line2`}
                            value={values.items?.[merchantIndex]?.address_line2 || ''}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            fullWidth
                            disabled={isSubmitting || readOnly}
                            margin="dense"
                            size="small"
                            color="secondary"
                        />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <Box display="flex" alignItems="center" justifyContent="flex-end" height="100%">
                            <Typography align="right">Address Line 3</Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={9}>
                        <TextField
                            label="Address Line 3"
                            name={`${prefix}.address_line3`}
                            value={values.items?.[merchantIndex]?.address_line3 || ''}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            fullWidth
                            disabled={isSubmitting || readOnly}
                            margin="dense"
                            size="small"
                            color="secondary"
                        />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <Box display="flex" alignItems="center" justifyContent="flex-end" height="100%">
                            <Typography align="right">City</Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={9}>
                        <TextField
                            label="City"
                            name={`${prefix}.city`}
                            value={values.items?.[merchantIndex]?.city || ''}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            fullWidth
                            disabled={isSubmitting || readOnly}
                            margin="dense"
                            size="small"
                            color="secondary"
                        />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <Box display="flex" alignItems="center" justifyContent="flex-end" height="100%">
                            <Typography align="right">Postal Code</Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={9}>
                        <TextField
                            label="Postal Code"
                            name={`${prefix}.postal_code`}
                            value={values.items?.[merchantIndex]?.postal_code || ''}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            fullWidth
                            disabled={isSubmitting || readOnly}
                            margin="dense"
                            size="small"
                            color="secondary"
                        />
                    </Grid>
                </>
            )}


        </Grid>
    );
};

export default MerchantForm;